import { GatewayControleQuinquennal } from "@conformite/gateway";
import {
  Divider,
  Dropdown,
  FilterSearch,
  InputSearch,
  ListTemplate,
  Pagination,
  SkeletonTable,
  TabButton,
  Table,
  TableSortIcon,
  TagWithIcon,
  TitleWithNumber,
  downloadPdf,
  buildCsv,
  Button,
  displayErrorToast,
  displayWarningToast,
  useDialog,
} from "@endya-conformite/ui-kit";
import ExportIcon from "@endya-conformite/ui-kit/src/assets/icons/export.svg?react";
import Loader from "@endya-conformite/ui-kit/src/assets/loader.svg?react";
import CloseIcon from "@endya-conformite/ui-kit/src/assets/icons/x-lg.svg?react";
import { ControleQuinquennalStatus } from "@gateway/controle-quinquennal/controller/controle-quinquennal.definition";
import {
  DateRange,
  FilterDateRange,
  FilterDropdown,
  FilterMultiSelect,
} from "@src/components/Filter";
import {
  CQFilters,
  CQView,
  DEFAULT_CQ_LIST_STATE,
  EMPTY_FILTERS,
  loadCQListState,
  saveCQListState,
} from "@src/pages/controle-quinquennal/cqListFilters.storage";
import { PageWithTitleTemplate } from "@src/components/templates/PageWithTitleTemplate";
import { useCQListQuery } from "@src/data/controle-quinquennal/hook/useCQListQuery";
import { useCQListAll } from "@src/data/controle-quinquennal/hook/useCQListAll";
import { useCancelCQBatchMutation } from "@src/data/controle-quinquennal/hook/useCancelCQBatchMutation";
import { useCQOriasRadieesQuery } from "@src/data/controle-quinquennal/hook/useCQOriasRadieesQuery";
import { useRelaunchOriasCheck } from "@src/data/infoSocieteOrias/hook/useRelaunchOriasCheck";
import { isRecette } from "@src/helper/environnement.helper";
import { downloadFile } from "@src/helper/file.helper";
import { useTableSort } from "@src/helper/table.helper";
import { cqDossierUrl } from "@src/pages/controle-quinquennal/controleQuinquennal.helper";
import {
  getControleQuinquennalDateButoir,
  isControleQuinquennalReadOnly,
  statusConfig,
  TICKET_STATUS_MAP,
  TicketStatus,
} from "@src/helper/ticket.helper";
import classNames from "classnames";
import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CSV_HEADERS, mapCQToExportRow } from "@src/helper/cq-export.helper";
import {
  CQOriasRadiee,
  filtrerOriasRadieesAffichees,
} from "@src/helper/cq-orias-radiees.helper";
import { useCQMetriquesQuery } from "@src/data/controle-quinquennal/hook/useCQMetriquesQuery";
import {
  LIBELLE_MES_CONTROLES,
  LIBELLE_TOUS_LES_CONTROLES,
  LIBELLE_TOUS_LES_CONTROLES_EN_COURS,
} from "@src/pages/controle-quinquennal/cqListe.wording";
import { CelluleMetrique } from "./components/CelluleMetrique";
import { CQCancelDialog } from "./components/CQCancelDialog";
import { CQOriasRadiees } from "./components/CQOriasRadiees";
import style from "./CQListPage.module.scss";

const PAGE_SIZE = 20;

const isRangeActive = (range: DateRange) =>
  Boolean(range.from) || Boolean(range.to);

const countActiveFilters = (filters: CQFilters) =>
  (filters.pipelineStages.length > 0 ? 1 : 0) +
  (filters.millesimes.length > 0 ? 1 : 0) +
  (isRangeActive(filters.dateDebut) ? 1 : 0) +
  (isRangeActive(filters.dateDepot) ? 1 : 0) +
  (isRangeActive(filters.dateFin) ? 1 : 0);

type PipelineStageKey = keyof typeof TICKET_STATUS_MAP;

const FERMES_STAGES: PipelineStageKey[] = [
  TicketStatus.FERME,
  TicketStatus.RADIE,
  TicketStatus.ANNULE,
];

const EN_COURS_HIDDEN_STAGES: PipelineStageKey[] = [
  TicketStatus.A_ASSIGNER,
  ...FERMES_STAGES,
];

const ALL_STAGES: PipelineStageKey[] = (
  Object.keys(TICKET_STATUS_MAP) as PipelineStageKey[]
).filter((key) => key !== TicketStatus.A_ASSIGNER);

const EN_COURS_STAGES: PipelineStageKey[] = ALL_STAGES.filter(
  (key) => !EN_COURS_HIDDEN_STAGES.includes(key)
);

const STAGES_BY_VIEW: Record<CQView, PipelineStageKey[]> = {
  enCours: EN_COURS_STAGES,
  tousEnCours: EN_COURS_STAGES,
  tous: ALL_STAGES,
};

const LIBELLE_BY_VIEW: Record<CQView, string> = {
  enCours: LIBELLE_MES_CONTROLES,
  tousEnCours: LIBELLE_TOUS_LES_CONTROLES_EN_COURS,
  tous: LIBELLE_TOUS_LES_CONTROLES,
};

/**
 * Le tri est délégué au back : une clé absente de cette liste produirait un
 * `orderBy` Prisma invalide. Les colonnes de métriques en sont exclues.
 */
const SERVER_SORTABLE_KEYS = [
  "millesime",
  "entrepriseName",
  "pipelineStage",
  "dateDebut",
  "dateDepot",
  "dateButoir",
];

const STAGE_DOT_CLASS = {
  success: style.stageDotSuccess,
  info: style.stageDotInfo,
  error: style.stageDotError,
} as const;

const buildPipelineStageOptions = (view: CQView) =>
  STAGES_BY_VIEW[view].map((key) => {
    const config = TICKET_STATUS_MAP[key];
    return {
      value: key,
      label: (
        <span className={style.stageOption}>
          <span
            className={classNames(
              style.stageDot,
              STAGE_DOT_CLASS[config.variant]
            )}
          />
          {config.label}
        </span>
      ),
    };
  });

const buildFilterQueryPart = (filters: CQFilters, view: CQView) => ({
  pipelineStages:
    filters.pipelineStages.length > 0
      ? filters.pipelineStages
      : STAGES_BY_VIEW[view],
  millesimes: filters.millesimes.length > 0 ? filters.millesimes : undefined,
  dateDebutFrom: filters.dateDebut.from,
  dateDebutTo: filters.dateDebut.to,
  dateDepotFrom: filters.dateDepot.from,
  dateDepotTo: filters.dateDepot.to,
  dateFinFrom: filters.dateFin.from,
  dateFinTo: filters.dateFin.to,
});

const useFilter = () => {
  const [initialState] = useState(loadCQListState);
  const [page, setPage] = useState(initialState.page);
  const sort = useTableSort({
    defaultSortBy: initialState.sortBy,
    defaultSortOrder: initialState.sortOrder,
  });
  const [search, setSearch] = useState(initialState.search);
  const [debouncedSearch, setDebouncedSearch] = useState(initialState.search);
  const [view, setViewState] = useState<CQView>(initialState.view);
  const [appliedFilters, setAppliedFilters] = useState<CQFilters>(
    initialState.appliedFilters
  );

  useEffect(() => {
    if (search === debouncedSearch) return undefined;

    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [search, debouncedSearch]);

  useEffect(() => {
    saveCQListState({
      page,
      sortBy: sort.sortBy ?? DEFAULT_CQ_LIST_STATE.sortBy,
      sortOrder: sort.sortOrder,
      search,
      view,
      appliedFilters,
    });
  }, [page, sort.sortBy, sort.sortOrder, search, view, appliedFilters]);

  const applyFilters = (next: CQFilters) => {
    setAppliedFilters(next);
    setPage(1);
  };

  const resetFilters = () => {
    setAppliedFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const setView = (next: CQView) => {
    if (next === view) return;
    setViewState(next);
    setAppliedFilters((prev) => ({
      ...prev,
      pipelineStages: prev.pipelineStages.filter((stage) =>
        STAGES_BY_VIEW[next].includes(stage as PipelineStageKey)
      ),
    }));
    setPage(1);
  };

  return {
    page,
    setPage,
    sort,
    search,
    setSearch,
    debouncedSearch,
    view,
    setView,
    appliedFilters,
    applyFilters,
    resetFilters,
  };
};

export function CQListPage() {
  const {
    page,
    setPage,
    sort: { sortBy, sortOrder, toggleSort, orderFor },
    search,
    setSearch,
    debouncedSearch,
    view,
    setView,
    appliedFilters,
    applyFilters,
    resetFilters,
  } = useFilter();
  const filterQueryPart = useMemo(
    () => buildFilterQueryPart(appliedFilters, view),
    [appliedFilters, view]
  );
  // Portée "tous" indépendante de l'onglet sélectionné : les compteurs des
  // onglets doivent rester stables quand on bascule de l'un à l'autre.
  // La portée par propriétaire suit la même règle, côté back.
  const compteursPipelineStages = useMemo(
    () => buildFilterQueryPart(appliedFilters, "tous").pipelineStages,
    [appliedFilters]
  );
  const pipelineStageOptions = useMemo(
    () => buildPipelineStageOptions(view),
    [view]
  );
  const { data, isPending, isError } = useCQListQuery({
    page,
    limit: PAGE_SIZE,
    sortField: sortBy,
    sortOrder: sortOrder ?? undefined,
    search: debouncedSearch,
    ...filterQueryPart,
    compteursPipelineStages,
    mesControles: view === "enCours",
  });
  const metriques = useCQMetriquesQuery(data?.data ?? []);
  const navigate = useNavigate();
  const oriasRadiees = useCQOriasRadieesQuery();
  const relaunchOriasCheck = useRelaunchOriasCheck(oriasRadiees.sirens);
  const fetchAllRows = useCQListAll();
  const [selectedOriasRadiees, setSelectedOriasRadiees] = useState<string[]>(
    []
  );
  const toggleOriasRadiee = (idControleQuinquennal: string) =>
    setSelectedOriasRadiees((prev) =>
      prev.includes(idControleQuinquennal)
        ? prev.filter((id) => id !== idControleQuinquennal)
        : [...prev, idControleQuinquennal]
    );
  const { openDialog } = useDialog();
  const cancelCQBatch = useCancelCQBatchMutation();
  const handleCancelOriasRadiees = (rows: CQOriasRadiee[]) => {
    if (rows.length === 0) return;
    void openDialog(CQCancelDialog, { rows }).then((confirmed) => {
      if (!confirmed) return;
      cancelCQBatch.mutate(
        rows.map(({ id }) => id),
        { onSuccess: () => setSelectedOriasRadiees([]) }
      );
    });
  };
  const [exportingFormat, setExportingFormat] = useState<"csv" | "pdf" | null>(
    null
  );
  const availableMillesimes = data?.availableMillesimes ?? [];
  const millesimeOptions = availableMillesimes.map((m) => ({
    value: String(m),
    label: String(m),
  }));
  const activeFilterCount = countActiveFilters(appliedFilters);

  const oriasRadieesFiltrees = useMemo(
    () =>
      filtrerOriasRadieesAffichees(oriasRadiees.rows, {
        ...filterQueryPart,
        search: debouncedSearch,
        mesControles: view === "enCours",
      }),
    [oriasRadiees.rows, filterQueryPart, debouncedSearch, view]
  );
  const controlesAffiches = useMemo(() => {
    const controles = data?.data ?? [];
    if (!isRecette) return controles;
    const idsRadies = new Set(oriasRadiees.rows.map(({ id }) => id));
    return controles.filter(({ id }) => !idsRadies.has(id));
  }, [data, oriasRadiees.rows]);

  const handleSearch = (searchInput: string) => {
    setSearch(searchInput);
    setPage(1);
  };

  const exportParams = {
    search: debouncedSearch,
    sortField: sortBy,
    sortOrder: sortOrder ?? undefined,
    ...filterQueryPart,
    mesControles: view === "enCours",
  };

  const handleExportCsv = async () => {
    setExportingFormat("csv");
    try {
      const rows = await fetchAllRows(exportParams);
      const csvContent = buildCsv(CSV_HEADERS, rows.map(mapCQToExportRow));
      const filename = `export_controles_quinquennaux_${format(new Date(), "dd-MM-yyyy")}.csv`;
      downloadFile(
        new Blob([csvContent], { type: "text/csv;charset=utf-8;" }),
        filename
      );
    } catch {
      displayErrorToast("Une erreur est survenue lors de l'export");
    } finally {
      setExportingFormat(null);
    }
  };

  const handleExportPdf = async () => {
    setExportingFormat("pdf");
    try {
      const rows = await fetchAllRows(exportParams);
      const title = `Contrôles quinquennaux — ${format(new Date(), "dd/MM/yyyy")}`;
      downloadPdf(title, CSV_HEADERS, rows.map(mapCQToExportRow));
    } catch {
      displayErrorToast("Une erreur est survenue lors de l'export");
    } finally {
      setExportingFormat(null);
    }
  };

  const handleRowClick = (
    row: Pick<
      GatewayControleQuinquennal.ControleQuinquennalAdminListResponse,
      "id" | "idEntreprise" | "status" | "pipelineStage"
    >
  ) => {
    if (
      row.status === ControleQuinquennalStatus.DEPOSE ||
      isControleQuinquennalReadOnly(row.pipelineStage)
    ) {
      navigate(cqDossierUrl(row.idEntreprise, row.id));
    } else {
      displayWarningToast(
        "Le contrôle quinquennal est indisponible pour le moment, en attente de l'adhérent."
      );
    }
  };

  return (
    <PageWithTitleTemplate title="" maxWidth="xxlarge">
      <TitleWithNumber title={LIBELLE_BY_VIEW[view]} number={data?.total} />
      <ListTemplate
        header={
          <>
            <FilterSearch
              search={
                <div className={style.viewTabs}>
                  <TabButton
                    label={LIBELLE_MES_CONTROLES}
                    state={view === "enCours" ? "selected" : "default"}
                    showTag
                    tagCount={data?.compteurs?.mesEnCours ?? 0}
                    onSelect={() => setView("enCours")}
                  />
                  <TabButton
                    label={LIBELLE_TOUS_LES_CONTROLES_EN_COURS}
                    state={view === "tousEnCours" ? "selected" : "default"}
                    showTag
                    tagCount={data?.compteurs?.enCours ?? 0}
                    onSelect={() => setView("tousEnCours")}
                  />
                  <TabButton
                    label={LIBELLE_TOUS_LES_CONTROLES}
                    state={view === "tous" ? "selected" : "default"}
                    showTag
                    tagCount={data?.compteurs?.total ?? 0}
                    onSelect={() => setView("tous")}
                  />
                </div>
              }
              action={
                <div className={style.filterActions}>
                  <Dropdown
                    content={<p>Exporter</p>}
                    icon={
                      exportingFormat !== null ? <Loader /> : <ExportIcon />
                    }
                    variant="text"
                    dismissOnClick
                    placement="bottom-end"
                    className={style.actionButton}
                  >
                    {({ close }) => (
                      <>
                        <Button
                          variant="text"
                          disabled={exportingFormat !== null}
                          onClick={() => {
                            close();
                            handleExportCsv().catch(() => {});
                          }}
                        >
                          {exportingFormat === "csv"
                            ? "Export en cours..."
                            : "Format CSV"}
                        </Button>
                        <Button
                          variant="text"
                          disabled={exportingFormat !== null}
                          onClick={() => {
                            close();
                            handleExportPdf().catch(() => {});
                          }}
                        >
                          {exportingFormat === "pdf"
                            ? "Export en cours..."
                            : "Format PDF"}
                        </Button>
                      </>
                    )}
                  </Dropdown>
                  <div className={style.filterGroup}>
                    <FilterDropdown<CQFilters>
                      activeCount={activeFilterCount}
                      applied={appliedFilters}
                      onApply={applyFilters}
                      className={style.actionButton}
                    >
                      {(draft, setDraft) => (
                        <div className={style.filterColumns}>
                          <div className={style.filterColumn}>
                            <FilterMultiSelect<string>
                              name="filter-pipelineStage"
                              label="État"
                              value={draft.pipelineStages}
                              onChange={(v) =>
                                setDraft((prev) => ({
                                  ...prev,
                                  pipelineStages: v,
                                }))
                              }
                              options={pipelineStageOptions}
                            />
                            {millesimeOptions.length > 0 && (
                              <FilterMultiSelect<number>
                                name="filter-millesime"
                                label="Millésime"
                                value={draft.millesimes}
                                onChange={(v) =>
                                  setDraft((prev) => ({
                                    ...prev,
                                    millesimes: v,
                                  }))
                                }
                                options={millesimeOptions}
                                parseValue={(raw) => Number(raw)}
                              />
                            )}
                          </div>
                          <div className={style.filterColumn}>
                            <FilterDateRange
                              name="filter-dateDebut"
                              label="Date de début"
                              value={draft.dateDebut}
                              onChange={(v) =>
                                setDraft((prev) => ({ ...prev, dateDebut: v }))
                              }
                            />
                            <FilterDateRange
                              name="filter-dateDepot"
                              label="Date de dépôt"
                              value={draft.dateDepot}
                              onChange={(v) =>
                                setDraft((prev) => ({ ...prev, dateDepot: v }))
                              }
                            />
                            <FilterDateRange
                              name="filter-dateFin"
                              label="Date de fin"
                              value={draft.dateFin}
                              onChange={(v) =>
                                setDraft((prev) => ({ ...prev, dateFin: v }))
                              }
                            />
                          </div>
                        </div>
                      )}
                    </FilterDropdown>
                    {activeFilterCount > 0 && (
                      <button
                        type="button"
                        className={style.clearFiltersButton}
                        onClick={resetFilters}
                        aria-label="Effacer les filtres"
                      >
                        <CloseIcon />
                      </button>
                    )}
                  </div>
                  <div className={style.searchWrapper}>
                    <InputSearch
                      name="search"
                      placeholder="Rechercher une entreprise"
                      value={search}
                      variant="full-width"
                      onChange={(e) => handleSearch(e.target.value)}
                      onClear={() => handleSearch("")}
                    />
                  </div>
                </div>
              }
            />
            <Divider className={style.headerDivider} />
          </>
        }
        table={
          <div className={style.tablesCard}>
            {isRecette && (
              <CQOriasRadiees
                rows={oriasRadieesFiltrees}
                lastUpdate={
                  relaunchOriasCheck.data?.lastUpdate ?? oriasRadiees.lastUpdate
                }
                hasError={
                  oriasRadiees.isError ||
                  relaunchOriasCheck.isError ||
                  oriasRadiees.dernierAppelEnEchec
                }
                isRelaunching={relaunchOriasCheck.isPending}
                onRelaunch={() => relaunchOriasCheck.mutate()}
                selected={selectedOriasRadiees}
                onToggleRow={toggleOriasRadiee}
                onClearSelection={() => setSelectedOriasRadiees([])}
                onCancelRows={handleCancelOriasRadiees}
                onRowClick={handleRowClick}
                isCancelling={cancelCQBatch.isPending}
              />
            )}
            {isPending ? (
              <SkeletonTable size={[1, 1]} />
            ) : (
              <Table
                className={style.cqTable}
                data={controlesAffiches}
                textNoData={
                  isError
                    ? "Une erreur est survenue lors du chargement des contrôles"
                    : "Aucun contrôle quinquennal trouvé"
                }
                onRowClick={handleRowClick}
                sort={(key) => {
                  if (SERVER_SORTABLE_KEYS.includes(key)) toggleSort(key);
                }}
                columns={[
                  {
                    key: "millesime",
                    title: "Millésime",
                    icon: <TableSortIcon order={orderFor("millesime")} />,
                    size: 6,
                    render: ({ millesime }) => <p>{millesime}</p>,
                  },
                  {
                    key: "entrepriseName",
                    title: "Entreprise",
                    icon: <TableSortIcon order={orderFor("entrepriseName")} />,
                    size: 8,
                    render: ({ entrepriseName }) => (
                      <p className={style.entreprise}>{entrepriseName}</p>
                    ),
                  },
                  {
                    key: "pipelineStage",
                    title: "État",
                    icon: <TableSortIcon order={orderFor("pipelineStage")} />,
                    size: 8,
                    render: (row) => {
                      const statusKey = row.pipelineStage;
                      const config =
                        TICKET_STATUS_MAP[statusKey] ??
                        statusConfig("info", "Inconnu");
                      return (
                        <TagWithIcon
                          overrideIcon={config.icon}
                          variant={config.variant}
                        >
                          {config.label}
                        </TagWithIcon>
                      );
                    },
                  },
                  {
                    key: "dateDebut",
                    title: "Date de début",
                    icon: <TableSortIcon order={orderFor("dateDebut")} />,
                    size: 6,
                    render: ({ dateDebut, dateCreation }) => (
                      <p>
                        {format(
                          new Date(dateDebut ?? dateCreation),
                          "dd/MM/yyyy"
                        )}
                      </p>
                    ),
                  },
                  {
                    key: "dateDepot",
                    title: "Date de dépôt",
                    icon: <TableSortIcon order={orderFor("dateDepot")} />,
                    size: 6,
                    render: ({ dateDepot }) => (
                      <p>
                        {dateDepot
                          ? format(new Date(dateDepot), "dd/MM/yyyy")
                          : "-"}
                      </p>
                    ),
                  },
                  {
                    key: "dateButoir",
                    title: "Date butoir",
                    icon: <TableSortIcon order={orderFor("dateButoir")} />,
                    size: 6,
                    render: (row) => {
                      const dateButoir = getControleQuinquennalDateButoir(row);
                      return (
                        <p>
                          {dateButoir ? format(dateButoir, "dd/MM/yyyy") : "-"}
                        </p>
                      );
                    },
                  },
                  {
                    key: "conformiteIa",
                    title: "Conformité IA",
                    icon: <TableSortIcon order={orderFor("conformiteIa")} />,
                    size: 6,
                    render: ({ id }) => (
                      <CelluleMetrique
                        metriques={metriques}
                        id={id}
                        render={({ conformiteIa }) =>
                          conformiteIa === null ? null : (
                            <p className={style.conformiteIa}>
                              {conformiteIa}%
                            </p>
                          )
                        }
                      />
                    ),
                  },
                  {
                    key: "conformiteVerificateur",
                    title: "Conformité vérificateur",
                    icon: (
                      <TableSortIcon
                        order={orderFor("conformiteVerificateur")}
                      />
                    ),
                    size: 7,
                    render: ({ id }) => (
                      <CelluleMetrique
                        metriques={metriques}
                        id={id}
                        render={({ conformiteVerificateur }) =>
                          conformiteVerificateur === null ? null : (
                            <p className={style.conformiteVerificateur}>
                              {conformiteVerificateur}%
                            </p>
                          )
                        }
                      />
                    ),
                  },
                  {
                    key: "avancement",
                    title: "Avancée du traitement",
                    icon: <TableSortIcon order={orderFor("avancement")} />,
                    size: 7,
                    render: ({ id }) => (
                      <CelluleMetrique
                        metriques={metriques}
                        id={id}
                        render={({ questionsRepondues, questionsTotal }) =>
                          questionsTotal === 0 ? null : (
                            <p
                              className={classNames(style.avancement, {
                                [style.avancementComplete]:
                                  questionsRepondues === questionsTotal,
                              })}
                            >
                              {questionsRepondues}/{questionsTotal}
                            </p>
                          )
                        }
                      />
                    ),
                  },
                ]}
                footer={
                  <Pagination
                    page={page - 1}
                    onChange={(p) => setPage(p + 1)}
                    pageSize={PAGE_SIZE}
                    total={data?.total ?? 0}
                  />
                }
              />
            )}
          </div>
        }
      />
    </PageWithTitleTemplate>
  );
}
